import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceIntelligenceBibleApi,
  type AttributionFactor,
  type BibleDashboard,
  type BibleStatus,
  type DNAComponent,
  type ExperimentStage,
  type FeatureAlignmentEvaluation,
  type HealthScoreDimension,
  type KnowledgeGraphNode,
  type LifecycleStage,
  type MissionOutcome,
  type NorthStarMetric,
  type PhilosophyQuestion,
  type ProductPillar,
  type SuccessMeasurementTemplate,
  type UXPrinciple,
} from '../../api/marketplace-intelligence-bible';

type Tab = 'overview' | 'mission' | 'pillars' | 'metrics' | 'ai' | 'dna' | 'health' | 'lifecycle' | 'status';
type Theme = 'light' | 'dark';

function priorityColor(priority: string, dark: boolean) {
  if (priority === 'high') return dark ? 'text-red-400' : 'text-red-600';
  if (priority === 'medium') return dark ? 'text-amber-400' : 'text-amber-600';
  return dark ? 'text-slate-400' : 'text-slate-500';
}

export function MarketplaceIntelligenceBiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<BibleDashboard | null>(null);
  const [outcomes, setOutcomes] = useState<MissionOutcome[]>([]);
  const [questions, setQuestions] = useState<PhilosophyQuestion[]>([]);
  const [metrics, setMetrics] = useState<NorthStarMetric[]>([]);
  const [pillars, setPillars] = useState<ProductPillar[]>([]);
  const [dna, setDna] = useState<DNAComponent[]>([]);
  const [healthDims, setHealthDims] = useState<HealthScoreDimension[]>([]);
  const [avgHealth, setAvgHealth] = useState(0);
  const [attribution, setAttribution] = useState<AttributionFactor[]>([]);
  const [expStages, setExpStages] = useState<ExperimentStage[]>([]);
  const [recLifecycle, setRecLifecycle] = useState<LifecycleStage[]>([]);
  const [kgNodes, setKgNodes] = useState<KnowledgeGraphNode[]>([]);
  const [ux, setUx] = useState<UXPrinciple[]>([]);
  const [successTpl, setSuccessTpl] = useState<SuccessMeasurementTemplate | null>(null);
  const [status, setStatus] = useState<BibleStatus | null>(null);
  const [evalResult, setEvalResult] = useState<FeatureAlignmentEvaluation | null>(null);
  const [featureName, setFeatureName] = useState('Bid Copilot');
  const [targetKPI, setTargetKPI] = useState('roas');
  const [pillarKey, setPillarKey] = useState('advertising_intelligence');
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, miss, phil, met, pil, dnaC, health, attr, exp, rec, kg, uxP, tpl, st] = await Promise.all([
        marketplaceIntelligenceBibleApi.dashboard(),
        marketplaceIntelligenceBibleApi.mission(),
        marketplaceIntelligenceBibleApi.philosophy(),
        marketplaceIntelligenceBibleApi.northStarMetrics(),
        marketplaceIntelligenceBibleApi.pillars(),
        marketplaceIntelligenceBibleApi.marketplaceDNA(),
        marketplaceIntelligenceBibleApi.healthScore(),
        marketplaceIntelligenceBibleApi.attribution(),
        marketplaceIntelligenceBibleApi.experimentLifecycle(),
        marketplaceIntelligenceBibleApi.recommendationLifecycle(),
        marketplaceIntelligenceBibleApi.knowledgeGraph(),
        marketplaceIntelligenceBibleApi.uxPrinciples(),
        marketplaceIntelligenceBibleApi.successMeasurement(),
        marketplaceIntelligenceBibleApi.status(),
      ]);
      setDashboard(dash);
      setOutcomes(miss);
      setQuestions(phil);
      setMetrics(met);
      setPillars(pil);
      setDna(dnaC);
      setHealthDims(health.dimensions);
      setAvgHealth(health.avg_score);
      setAttribution(attr);
      setExpStages(exp);
      setRecLifecycle(rec);
      setKgNodes(kg);
      setUx(uxP);
      setSuccessTpl(tpl);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load marketplace intelligence bible');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const evaluateFeature = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceIntelligenceBibleApi.evaluateFeature({
        feature_name: featureName,
        target_kpi: targetKPI,
        pillar_key: pillarKey,
      });
      setEvalResult(result);
      setActionMsg(result.aligned ? 'Feature aligned with MIB-000001' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Evaluation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'mission', label: 'Mission & Philosophy' },
    { id: 'pillars', label: 'Product Pillars' },
    { id: 'metrics', label: 'North Star Metrics' },
    { id: 'ai', label: 'AI & Experiments' },
    { id: 'dna', label: 'Marketplace DNA' },
    { id: 'health', label: 'Health Score' },
    { id: 'lifecycle', label: 'Lifecycles' },
    { id: 'status', label: 'Bible Status' },
  ];

  const primaryMetrics = metrics.filter((m) => m.category === 'primary');
  const secondaryMetrics = metrics.filter((m) => m.category === 'secondary');

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-orange-500">MIB-000001</p>
          <h1 className="text-2xl font-semibold">Marketplace Intelligence Bible</h1>
          <p className={`text-sm ${muted}`}>
            Vision, product philosophy, AI strategy, growth framework and marketplace operating model
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/amazon/market-intel" className="text-orange-500 hover:underline">Market Intelligence</Link>
            <span className={muted}>·</span>
            <Link to="/platform/product-family-constitution" className="text-orange-500 hover:underline">Product Family Constitution</Link>
            <span className={muted}>·</span>
            <Link to="/amazon/growth" className="text-orange-500 hover:underline">Growth Intelligence</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-orange-600 text-white' : `${card} ${muted}`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Bible ID', value: dashboard.bible_id },
              { label: 'Product Pillars', value: dashboard.pillars },
              { label: 'North Star Metrics', value: dashboard.north_star_metrics },
              { label: 'Marketplaces', value: dashboard.marketplaces },
              { label: 'Avg Health Score', value: `${dashboard.avg_health_score.toFixed(1)}` },
              { label: 'Active Experiments', value: dashboard.active_experiments },
              { label: 'Open Recommendations', value: dashboard.open_recommendations },
              { label: 'Status', value: 'Mandatory Active' },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{item.label}</p>
                <p className="mt-1 text-2xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Supported Marketplaces</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {dashboard.marketplaces_list.map((m) => (
                <span key={m.key} className="rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-800 dark:bg-orange-900 dark:text-orange-200">{m.name}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'mission' && (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h2 className="mb-3 text-lg font-semibold">Mission Outcomes</h2>
            <div className="space-y-2">
              {outcomes.map((o) => (
                <div key={o.key} className={`flex items-center justify-between rounded-lg border px-4 py-2 ${card}`}>
                  <span>{o.name}</span>
                  <span className={`text-xs uppercase ${o.direction === 'increase' ? 'text-emerald-500' : 'text-sky-500'}`}>{o.direction}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="mb-3 text-lg font-semibold">Product Philosophy</h2>
            <p className={`mb-3 text-sm ${muted}`}>Every interaction SHALL answer:</p>
            <div className="space-y-2">
              {questions.map((q) => (
                <div key={q.key} className={`rounded-lg border px-4 py-3 ${card}`}>
                  <span className="text-orange-500">{q.order}.</span> {q.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'pillars' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pillars.map((p) => (
            <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="text-xs font-semibold text-orange-500">Pillar {p.id}</p>
              <h3 className="mt-1 font-medium">{p.name}</h3>
              <p className="mt-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">{p.goal}</p>
              {p.description && <p className={`mt-2 text-sm ${muted}`}>{p.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'metrics' && (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h2 className="mb-3 text-lg font-semibold">Primary Metrics</h2>
            <div className="flex flex-wrap gap-2">
              {primaryMetrics.map((m) => (
                <span key={m.key} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{m.name}</span>
              ))}
            </div>
          </div>
          <div>
            <h2 className="mb-3 text-lg font-semibold">Secondary Metrics</h2>
            <div className="flex flex-wrap gap-2">
              {secondaryMetrics.map((m) => (
                <span key={m.key} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{m.name}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'ai' && (
        <div className="space-y-6">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Attribution Factors</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-3">
              {attribution.map((f) => (
                <div key={f.key} className="flex justify-between text-sm">
                  <span>{f.name}</span>
                  <span className={muted}>{f.impact_pct?.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">Knowledge Graph Nodes</h3>
              <ul className={`mt-2 space-y-1 text-sm ${muted}`}>
                {kgNodes.map((n) => (
                  <li key={n.key}>{n.name}{n.connections && n.connections.length > 0 && ` → ${n.connections.join(', ')}`}</li>
                ))}
              </ul>
            </div>
            <div className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">UX Principles</h3>
              <ul className="mt-2 space-y-2">
                {ux.map((u) => (
                  <li key={u.key} className="text-sm"><span className="font-medium">{u.question}</span>{u.description && <span className={muted}> — {u.description}</span>}</li>
                ))}
              </ul>
            </div>
          </div>
          {successTpl && (
            <div className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">Success Measurement Template</h3>
              <p className={`mt-1 text-sm ${muted}`}>{successTpl.description}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {successTpl.required_fields.map((f) => (
                  <span key={f} className="rounded bg-orange-100 px-2 py-0.5 font-mono text-xs dark:bg-orange-900">{f}</span>
                ))}
              </div>
            </div>
          )}
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Feature Alignment Check</h3>
            <div className="mt-3 flex flex-wrap gap-3">
              <input value={featureName} onChange={(e) => setFeatureName(e.target.value)} placeholder="Feature name" className={`rounded-lg border px-3 py-2 text-sm ${card}`} />
              <input value={targetKPI} onChange={(e) => setTargetKPI(e.target.value)} placeholder="Target KPI" className={`rounded-lg border px-3 py-2 text-sm ${card}`} />
              <select value={pillarKey} onChange={(e) => setPillarKey(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${card}`}>
                {pillars.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
              </select>
              <button type="button" disabled={busy} onClick={evaluateFeature} className="rounded-lg bg-orange-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                {busy ? 'Checking…' : 'Evaluate Feature'}
              </button>
            </div>
            {evalResult && (
              <p className={`mt-3 text-sm ${evalResult.aligned ? 'text-emerald-500' : 'text-amber-500'}`}>
                {evalResult.aligned ? 'Aligned' : 'Not aligned'} — KPI: {evalResult.target_kpi}, Pillar: {evalResult.pillar}
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'dna' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {dna.map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{c.name}</h3>
                <span className="text-lg font-semibold text-orange-500">{c.score?.toFixed(1)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'health' && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs uppercase ${muted}`}>Overall Marketplace Health</p>
            <p className="text-3xl font-semibold text-orange-500">{avgHealth.toFixed(1)}</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {healthDims.map((d) => (
              <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">{d.name}</h3>
                  <span className={`text-xs uppercase ${priorityColor(d.priority, dark)}`}>{d.priority}</span>
                </div>
                <div className="mt-2 flex gap-4 text-sm">
                  <span>Current: <strong>{d.current_value}</strong></span>
                  <span>Target: <strong>{d.target_value}</strong></span>
                  <span>Gap: <strong>{d.gap}</strong></span>
                </div>
                {d.recommended_actions && d.recommended_actions.length > 0 && (
                  <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                    {d.recommended_actions.map((a) => <li key={a}>{a}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'lifecycle' && (
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h2 className="mb-3 text-lg font-semibold">Experiment Lifecycle</h2>
            <ol className="space-y-1">
              {expStages.map((s) => (
                <li key={s.key} className={`flex gap-2 rounded-lg border px-4 py-2 text-sm ${card}`}>
                  <span className="text-orange-500">{s.order}.</span> {s.name}
                </li>
              ))}
            </ol>
          </div>
          <div>
            <h2 className="mb-3 text-lg font-semibold">Recommendation Lifecycle</h2>
            <ol className="space-y-1">
              {recLifecycle.map((s) => (
                <li key={s.key} className={`flex gap-2 rounded-lg border px-4 py-2 text-sm ${card}`}>
                  <span className="text-orange-500">{s.order}.</span> {s.name}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {tab === 'status' && status && dashboard && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <div className="grid gap-4 md:grid-cols-2">
            {Object.entries(status).map(([key, value]) => (
              <div key={key}>
                <p className={`text-xs uppercase ${muted}`}>{key.replace(/_/g, ' ')}</p>
                <p className="mt-1 font-medium">{String(value)}</p>
              </div>
            ))}
          </div>
          {dashboard.extension_points.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium">Product Pillar Extension Points</h3>
              <ul className={`mt-2 space-y-2 text-sm ${muted}`}>
                {dashboard.extension_points.map((ep) => (
                  <li key={ep.pillar_key}>
                    <span className="font-mono text-xs text-orange-500">{ep.pillar_key}</span> — {ep.name}: {ep.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
